import React, { useEffect, useState } from 'react';
import { Modal } from '@ofself/shell';
import { useToast } from '../../components/common';
import { useConfirm } from '../../components/nucleus/actions';
import { loadVersions, revert, findNodes, relate, isBelongsTo, schemaOf, titleOf, tintOf } from '../nucleus/GraphModel';
import { Row } from '../../components/nucleus/Collection';
import { NodeMark } from '../../components/nucleus/faces';
import { SheetLabel } from '../nucleus/GraphSheets';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { FindBar } from '../../components/nucleus/FacetRail';
import { ago } from '../../nucleus/format';

// A NODE'S HISTORY — every version, newest first, each one readable as it
// was. Going back to one is an act that matters but can be undone (the
// version you leave stays in the history), so it asks first, softly.
export function NodeHistory({ group, node, canRevert, revertHint, onReverted, onClose }) {
    const confirm = useConfirm();
    const toast = useToast();
    const [rows, setRows] = useState(null);
    const [picked, setPicked] = useState(null);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState(null);
    const load = () => loadVersions(group, node.id)
        .then((r) => { setRows(r); setPicked((p) => p ?? r[0]?.version ?? null); })
        .catch((e) => { setRows([]); setProblem(e?.message || 'Its history didn’t load.'); });
    useEffect(() => { load(); }, [group, node.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const top = rows?.[0]?.version;
    const chosen = rows?.find((v) => v.version === picked);
    const back = async (v) => {
        const ok = await confirm({
            title: `Revert “${titleOf(node)}” to v${v.version}?`,
            body: `Its title and fields go back to how they were in v${v.version}. What it says now stays in its history, so you can come back to it.`,
            label: 'Revert',
            danger: false,
        });
        if (!ok) return;
        setBusy(true);
        try {
            await revert(group, node.id, v.version);
            toast.success?.(`Reverted to v${v.version}.`);
            setPicked(null);
            await load();
            onReverted?.();
        } catch (e) { toast.error?.(e?.message || 'Couldn’t revert it.'); }
        setBusy(false);
    };
    return (
        <Modal title={`History of “${titleOf(node)}”`} onClose={onClose}>
            <div className="graph-sheet">
                {rows && !rows.length && <p className="graph-sheet__lead">{problem || 'No history yet.'}</p>}
                <div className="graph-versions">
                    {(rows || []).map((v) => (
                        <div key={v.id || v.version} className={`graph-version${picked === v.version ? ' is-on' : ''}`}>
                            <button type="button" className="graph-version__press" onClick={() => setPicked(v.version)} aria-pressed={picked === v.version}>
                                <span className="graph-version__n">v{v.version}</span>
                                <span className="graph-version__what">
                                    {[v.change_type, (v.changed_fields || []).join(', '), v.change_reason].filter(Boolean).join(' · ') || 'changed'}
                                    <small>{[v.changed_by_type, ago(v.changed_at, { dateAfter: 7 })].filter(Boolean).join(' · ')}</small>
                                </span>
                            </button>
                            {canRevert && v.version !== top && (
                                <button type="button" className="graph-link" disabled={busy} onClick={() => back(v)}>Revert to this</button>
                            )}
                            {/* a member without edit may read the history, not go back in it (no author exception) */}
                            {!canRevert && revertHint && v.version !== top && (
                                <span className="graph-link-cell" title={revertHint}>
                                    <button type="button" className="graph-link" disabled aria-label={`Revert to this. ${revertHint}`}>Revert to this</button>
                                </span>
                            )}
                        </div>
                    ))}
                </div>
                {chosen?.full_snapshot && (
                    <>
                        <SheetLabel>v{chosen.version} as it was</SheetLabel>
                        <pre className="graph-code-view">{JSON.stringify({ title: chosen.full_snapshot.title, value_json: chosen.full_snapshot.value_json || {} }, null, 2)}</pre>
                    </>
                )}
                <SheetFoot><FootButton primary onClick={onClose}>Done</FootButton></SheetFoot>
            </div>
        </Modal>
    );
}

/** Link “x” to… — a type and a node found in the same space. The link is a node of
 *  its own, on the relationship schema (a hypergraph: links are nodes), so another
 *  link may be picked as its other end. */
export function RelateSheet({ group, node, onDone, onClose }) {
    const [q, setQ] = useState('');
    const [type, setType] = useState('relates_to');
    const [found, setFound] = useState([]);
    const [error, setError] = useState(null);
    useEffect(() => {
        let live = true;
        const t = setTimeout(() => findNodes(group, q.trim()).then((r) => { if (live) setFound(r); }).catch(() => { if (live) setFound([]); }), 200);
        return () => { live = false; clearTimeout(t); };
    }, [group, q]);
    const pick = async (other) => {
        setError(null);
        try { await relate(group, node.id, other.id, type.trim() || 'relates_to'); onDone?.(); } catch (e) { setError(e?.message || 'Couldn’t link them.'); }
    };
    return (
        <Modal title={`Link “${titleOf(node)}” to…`} onClose={onClose}>
            <div className="graph-sheet">
                <div className="graph-sheet__row">
                    <input className="graph-input is-mono graph-relate__type" aria-label="Relationship" placeholder="relationship" value={type} onChange={(e) => setType(e.target.value)} />
                    <FindBar compact autoFocus className="graph-relate__find" value={q} onChange={setQ} placeholder="Find a node" />
                </div>
                <div className="graph-snapshot nx-rows">
                    {found.filter((n) => n.id !== node.id).map((n) => (
                        <Row key={n.id} lead={<NodeMark schema={schemaOf(n)} schemaId={n.schema_id} hue={tintOf(schemaOf(n))} />}
                            name={titleOf(n)} end={schemaOf(n)} onPress={() => pick(n)} />
                    ))}
                </div>
                {isBelongsTo(type) && (
                    <p className="graph-sheet__lead" role="note">
                        belongs_to is not just a label: “{titleOf(node)}” will be seen by everyone who can see the node you pick.
                    </p>
                )}
                {error && <p className="graph-sheet__error">{error}</p>}
                <SheetFoot><FootButton onClick={onClose}>Cancel</FootButton></SheetFoot>
            </div>
        </Modal>
    );
}

/** Anything as JSON, as it is stored (sealed fields stay sealed). */
export function JsonSheet({ title, value, onClose }) {
    return (
        <Modal title={title} onClose={onClose}>
            <div className="graph-sheet">
                <pre className="graph-code-view is-tall">{JSON.stringify(value ?? {}, null, 2)}</pre>
                <SheetFoot><FootButton primary onClick={onClose}>Done</FootButton></SheetFoot>
            </div>
        </Modal>
    );
}
